/**
 * Push seam for the customer app (W18).
 *
 * `@capacitor/push-notifications` is not a dependency of this app yet, so this file talks to the
 * native plugin by name through `registerPlugin('PushNotifications')`. When the plugin is added to
 * the Android project (npm install + `cap sync`) these calls start working with no code change;
 * until then — and in every plain browser — each call is a safe no-op that returns null.
 *
 * Web push needs a Firebase web app + VAPID key (not configured yet), so the web path is: no
 * token, and the in-app inbox (bell + unread count) carries every message instead.
 */
import { Capacitor, registerPlugin, type PluginListenerHandle } from '@capacitor/core';
import { PUSH_DATA_KEYS, type PushPlatform } from '@capsico/contracts';

type PermissionState = 'prompt' | 'prompt-with-rationale' | 'granted' | 'denied';

interface PushToken {
  value: string;
}

interface PushActionPerformed {
  actionId: string;
  notification: { data?: Record<string, unknown> };
}

interface PushNotificationsPlugin {
  checkPermissions(): Promise<{ receive: PermissionState }>;
  requestPermissions(): Promise<{ receive: PermissionState }>;
  register(): Promise<void>;
  addListener(
    event: 'registration',
    listener: (token: PushToken) => void,
  ): Promise<PluginListenerHandle>;
  addListener(
    event: 'registrationError',
    listener: (error: { error: string }) => void,
  ): Promise<PluginListenerHandle>;
  addListener(
    event: 'pushNotificationActionPerformed',
    listener: (action: PushActionPerformed) => void,
  ): Promise<PluginListenerHandle>;
}

const PushNotifications = registerPlugin<PushNotificationsPlugin>('PushNotifications');

function plugin(): PushNotificationsPlugin | null {
  if (!Capacitor.isNativePlatform()) return null;
  return Capacitor.isPluginAvailable('PushNotifications') ? PushNotifications : null;
}

function platform(): PushPlatform {
  return Capacitor.getPlatform() === 'ios' ? 'ios' : 'android';
}

const TOKEN_WAIT_MS = 15_000;

/**
 * Asks for permission (once — Android remembers the answer) and returns this phone's FCM token,
 * or null when push is not available, denied, or the token does not arrive in time.
 */
export async function obtainPushToken(): Promise<{ token: string; platform: PushPlatform } | null> {
  const push = plugin();
  if (!push) return null;
  try {
    let state = (await push.checkPermissions()).receive;
    if (state === 'prompt' || state === 'prompt-with-rationale') {
      state = (await push.requestPermissions()).receive;
    }
    if (state !== 'granted') return null;
    const handles: PluginListenerHandle[] = [];
    const token = await new Promise<string | null>((resolve) => {
      const timer = window.setTimeout(() => resolve(null), TOKEN_WAIT_MS);
      void push
        .addListener('registration', (value) => {
          window.clearTimeout(timer);
          resolve(value.value);
        })
        .then((handle) => handles.push(handle));
      void push
        .addListener('registrationError', () => {
          window.clearTimeout(timer);
          resolve(null);
        })
        .then((handle) => handles.push(handle));
      void push.register().catch(() => {
        window.clearTimeout(timer);
        resolve(null);
      });
    });
    for (const handle of handles) void handle.remove();
    return token && token.length >= 20 ? { token, platform: platform() } : null;
  } catch {
    return null;
  }
}

/**
 * Calls `listener` with the notification's deep link when the customer taps a push.
 * Returns an unsubscribe function (a no-op outside the native app).
 */
export function onPushTap(listener: (link: string) => void): () => void {
  const push = plugin();
  if (!push) return () => undefined;
  let handle: PluginListenerHandle | null = null;
  let cancelled = false;
  void push
    .addListener('pushNotificationActionPerformed', (action) => {
      const link = action.notification.data?.[PUSH_DATA_KEYS.link];
      if (typeof link === 'string' && link) listener(link);
    })
    .then((value) => {
      if (cancelled) void value.remove();
      else handle = value;
    })
    .catch(() => undefined);
  return () => {
    cancelled = true;
    if (handle) void handle.remove();
  };
}
